import {
  AutoMode as AutoModeIcon,
  WarningAmber as WarningAmberIcon,
  InfoOutlined as InfoOutlinedIcon,
  Block as BlockIcon,
  CreditCard as CreditCardIcon,
  PublishedWithChanges as PublishedWithChangesIcon,
  People as PeopleIcon,
  QueryStats as QueryStatsIcon,
} from '@mui/icons-material';
import {
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { every, find, includes, map, negate } from 'lodash';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutateOptions, useQuery, useQueryClient } from 'react-query';

import { requisitesApi, tradersApi } from 'api';
import { requisitesGroupApi } from 'api/requisites-groups.api';
import {
  DataGridColumnDefinition,
  RequisitesInfo,
  dataGridColumns,
  Dialog,
  CloseDialogResult,
  DescriptionRow,
  RequisitesStatusInfo,
  DateLabel,
  CrudPage,
  QrCode,
  RequisitesVerificationInfo,
  BatchActionButton,
} from 'components';
import { env } from 'config';
import { ERROR_MESSAGE } from 'constants/common.constants';
import {
  FilterDefinitionType,
  QueryKey,
  RequisitesStatus,
  StatusCode,
  OrderAutomationType,
  RequisitesVerificationStatus,
  RequisitesTabs,
} from 'enums';
import { BANKS_WITH_PAYMENT_LIMITS } from 'features/common-limits/constants';
import {
  useCurrencies,
  useMutation,
  usePartialQuery,
  usePayinRequisitesUtils,
  useQueryFilters,
  useUser,
  useUserContext,
  useUserContextUtils,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  Requisites,
  RequisitesGroupQrCodeData,
  RequisitesFilters,
} from 'types';
import { filtersUtils, formUtils, formatUtils, requisitesUtils } from 'utils';

import { BatchBlockRequisitesDialog } from '../BatchBlockRequisitesDialog';
import { BatchManagerRequisitesVerificationDialog } from '../BatchManagerRequisitesVerificationDialog';
import { BatchUnblockRequisitesDialog } from '../BatchUnblockRequisitesDialog';
import { BlockRequisitesDialog } from '../BlockRequisitesDialog';
import { ManagerRequisitesVerificationDialog } from '../ManagerRequisitesVerificationDialog';
import { PayinLimitsInfoDialog } from '../PayinLimitsInfoDialog';
import { RequisitesStatisticsDialog } from '../RequisitesStatisticsDialog';
import { TraderRequisitesVerificationDialog } from '../TraderRequisitesVerificationDialog';
import { UnblockRequisitesDialog } from '../UnblockRequisitesDialog';

type Values = {
  orderAutomationType: OrderAutomationType | undefined;
};

type Props = {
  archived?: boolean;
};

export const RequisitesList: FC<Props> = ({ archived }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_list',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const queryClient = useQueryClient();
  const { filters } = useQueryFilters<RequisitesFilters>();

  const { isOperator, isTrader, isAdminOrTechOperator, isManager, role } =
    useUser();
  const { banks, paymentTypes } = useUserContext();

  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();

  const [selectedOrderAutomationType, setSelectedOrderAutomationType] =
    useState<OrderAutomationType>();

  const [
    requisitesVerificationDialogProps,
    setRequisitesVerificationDialogProps,
  ] = useState<{ open: boolean; requisites?: Requisites }>({
    open: false,
  });

  const [
    batchRequisitesVerificationDialogProps,
    setBatchRequisitesVerificationDialogProps,
  ] = useState<{ open: boolean; count?: number }>({
    open: false,
  });

  const [limitsInfoDialogProps, setLimitsInfoDialogOpen] = useState<{
    open: boolean;
    requisites?: Requisites;
  }>({ open: false });

  const [orderAutomationDialogProps, setOrderAutomationDialogProps] = useState<{
    open: boolean;
    data?: Requisites;
    qrCodeData?: RequisitesGroupQrCodeData;
  }>({ open: false });

  const [blockDialogProps, setBlockDialogProps] = useState<{
    open: boolean;
    requisites?: Requisites;
  }>({ open: false });

  const [batchBlockDialogProps, setBatchBlockDialogProps] = useState<{
    open: boolean;
    count?: number;
  }>({ open: false });

  const [unblockDialogProps, setUnblockDialogProps] = useState<{
    open: boolean;
    requisites?: Requisites;
  }>({ open: false });

  const [batchUnblockDialogProps, setBatchUnblockDialogProps] = useState<{
    open: boolean;
    count?: number;
  }>({ open: false });

  const [confirmRemoveDialogProps, setConfirmRemoveDialogProps] = useState<{
    open: boolean;
    data?: Requisites;
  }>({ open: false });

  const [statisticsDialogProps, setStatisticsDialogProps] = useState<{
    open: boolean;
    requisites?: Requisites;
  }>({ open: false });

  const queryFn = useMemo(() => {
    if (isAdminOrTechOperator) {
      if (archived) {
        return requisitesApi.getAllPaginatedArchived;
      }
      return requisitesApi.getAllPaginated;
    }

    if (isOperator) {
      if (archived) {
        return requisitesApi.getAllPaginatedAsOperatorArchived;
      }
      return requisitesApi.getAllPaginatedAsOperator;
    }

    if (archived) {
      return requisitesApi.getAllMyPaginatedArchived;
    }

    return requisitesApi.getAllMyPaginated;
  }, [archived, isAdminOrTechOperator, isOperator]);

  const queryResult = usePartialQuery(QueryKey.Requisites, queryFn);

  const { data: groups = [] } = useQuery(
    QueryKey.RequisitesGroups,
    requisitesGroupApi.getAllAsRole(role),
  );

  const queryResultTraders = useQuery(
    QueryKey.Traders,
    () => tradersApi.getAllAsRole(role)(),
    {
      enabled: isManager,
    },
  );

  const { mutate: archive, isLoading } = useMutation(requisitesApi.archive, {
    onSuccess: () => {
      setConfirmRemoveDialogProps({ open: false });
      queryClient.invalidateQueries(QueryKey.Requisites);
    },
    notifierType: 'remove',
    notifierMessages: {
      error: (error: AxiosError<{ message: string | undefined }>) => {
        const status = error?.response?.status;
        if (
          status === StatusCode.Conflict &&
          error.response?.data?.message === ERROR_MESSAGE.ENTITY_IN_USE
        ) {
          return t('remove_dialog.errors.in_use');
        }
      },
    },
  });

  const { mutate: updateStatus } = useMutation<
    Requisites,
    AxiosError,
    { id: string; requisites: Partial<Requisites> }
  >(requisitesApi.updateStatus, {
    notifierType: 'execute',
  });

  const getGroup = useCallback((id?: string) => find(groups, { id }), [groups]);

  const selectedRequisiteGroup = useMemo(
    () => getGroup(orderAutomationDialogProps?.data?.groupId),
    [getGroup, orderAutomationDialogProps?.data?.groupId],
  );

  const handleStatusChange = useCallback(
    (
      id: string,
      status: RequisitesStatus,
      options?: MutateOptions<any, any, any, any>,
    ) =>
      updateStatus(
        {
          id,
          requisites: { status },
        },
        options,
      ),
    [updateStatus],
  );

  const handleOrderAutomation = useCallback((item: Requisites) => {
    setOrderAutomationDialogProps({
      open: true,
      data: item,
      qrCodeData: {
        host: new URL(env.apiUrl).hostname,
        apiKey: item.automationApiKey,
      },
    });
    setSelectedOrderAutomationType(item.orderAutomationType);
  }, []);

  const { handleOpenPage, detailsUrl, customerLimitsUrl } =
    usePayinRequisitesUtils();

  const handleEdit = useCallback(
    (item: Requisites) => {
      handleOpenPage(item.id, detailsUrl, {
        state: {
          groupId: item.groupId,
          tab: RequisitesTabs.Requisites,
        },
      });
    },
    [detailsUrl, handleOpenPage],
  );

  const handleCustomerLimits = useCallback(
    (item: Requisites) => {
      handleOpenPage(item.id, customerLimitsUrl, {
        state: {
          tab: RequisitesTabs.Requisites,
        },
      });
    },
    [customerLimitsUrl, handleOpenPage],
  );

  const handleStatistics = useCallback((item: Requisites) => {
    setStatisticsDialogProps({ open: true, requisites: item });
  }, []);

  const handleRemove = useCallback((item: Requisites) => {
    setConfirmRemoveDialogProps({ open: true, data: item });
  }, []);

  const { isTradeMethodSupported } = useUserContextUtils();

  const handleAutomationTypeChange = useCallback(
    (e: any, value: OrderAutomationType) => {
      if (value) {
        setSelectedOrderAutomationType(value);
      }
    },
    [setSelectedOrderAutomationType],
  );

  const handleCloseUnblockDialog = useCallback(
    (result: CloseDialogResult) => {
      if (result.ok) {
        queryClient.invalidateQueries(QueryKey.Requisites);
      }
      setUnblockDialogProps({ open: false });
    },
    [queryClient],
  );

  const handleCloseBatchUnblockDialog = useCallback(
    (result: CloseDialogResult<Requisites>) => {
      if (result.ok) {
        queryClient.invalidateQueries(QueryKey.Requisites);
      }
      setBatchUnblockDialogProps({ open: false });
    },
    [queryClient],
  );

  const handleCloseBlockDialog = useCallback(
    (result: CloseDialogResult<Requisites>) => {
      if (result.ok) {
        queryClient.invalidateQueries(QueryKey.Requisites);
      }
      setBlockDialogProps({ open: false });
    },
    [queryClient],
  );

  const handleCloseBatchBlockDialog = useCallback(
    (result: CloseDialogResult<Requisites>) => {
      if (result.ok) {
        queryClient.invalidateQueries(QueryKey.Requisites);
      }
      setBatchBlockDialogProps({ open: false });
    },
    [queryClient],
  );

  const renderVerifyRequisitesAction = useCallback(
    (requisites: Requisites) => {
      const expired =
        !requisites.verificationStatus ||
        requisites.verificationStatus === RequisitesVerificationStatus.Expired;
      const disabled = !isTradeMethodSupported(requisites);

      const traderSkipVerification =
        isTrader && !!requisites.trader?.skipPayinRequisitesVerification;

      if (traderSkipVerification) {
        return null;
      }

      return (
        <Tooltip
          title={
            disabled
              ? tCommon(
                  'features.requisites.verification.trade_method_unavailable',
                )
              : t('table.columns.verification')
          }
        >
          <IconButton
            onClick={() => {
              !disabled &&
                setRequisitesVerificationDialogProps({
                  open: true,
                  requisites,
                });
            }}
          >
            <PublishedWithChangesIcon
              color={disabled ? 'error' : expired ? 'warning' : 'secondary'}
            />
          </IconButton>
        </Tooltip>
      );
    },
    [isTradeMethodSupported, t, isTrader, tCommon],
  );

  const renderBlockRequisitesAction = useCallback(
    (requisites: Requisites) =>
      isManager &&
      requisites.status !== RequisitesStatus.Blocked && (
        <IconButton
          color="error"
          onClick={() => setBlockDialogProps({ open: true, requisites })}
        >
          <BlockIcon />
        </IconButton>
      ),
    [isManager],
  );

  const renderUnblockRequisitesAction = useCallback(
    (requisites: Requisites) =>
      isManager &&
      requisites.status === RequisitesStatus.Blocked && (
        <IconButton
          color="success"
          onClick={() => setUnblockDialogProps({ open: true, requisites })}
        >
          <CreditCardIcon />
        </IconButton>
      ),
    [isManager],
  );

  const renderBatchVerifyAction = useCallback(() => {
    const noRequisitesToUpdate = every(
      queryResult.data?.items,
      negate(isTradeMethodSupported),
    );
    return (
      <BatchActionButton
        tooltip={
          filters?.traderId
            ? noRequisitesToUpdate
              ? t('actions.no_requisites_to_update')
              : t('actions.batch_verify_requisites')
            : t('actions.trader_filter_required')
        }
        Icon={PublishedWithChangesIcon}
        color="secondary"
        onClick={() =>
          setBatchRequisitesVerificationDialogProps({
            open: true,
            count: queryResult.data?.count,
          })
        }
        disabled={!filters?.traderId || noRequisitesToUpdate}
      />
    );
  }, [t, filters?.traderId, queryResult.data, isTradeMethodSupported]);

  const renderBatchBlockAction = useCallback(() => {
    const noRequisitesToUpdate = every(queryResult.data?.items, {
      status: RequisitesStatus.Blocked,
    });
    return (
      <BatchActionButton
        tooltip={
          filters?.traderId
            ? noRequisitesToUpdate
              ? t('actions.no_requisites_to_update')
              : t('actions.batch_block_requisites')
            : t('actions.trader_filter_required')
        }
        Icon={BlockIcon}
        color="error"
        onClick={() =>
          setBatchBlockDialogProps({
            open: true,
            count: queryResult.data?.count,
          })
        }
        disabled={!filters?.traderId || noRequisitesToUpdate}
      />
    );
  }, [t, filters?.traderId, queryResult.data]);

  const renderBatchUnblockAction = useCallback(() => {
    const noRequisitesToUpdate = every(
      queryResult.data?.items,
      (requisites: Requisites) =>
        requisites.status !== RequisitesStatus.Blocked,
    );
    return (
      <BatchActionButton
        tooltip={
          filters?.traderId
            ? noRequisitesToUpdate
              ? t('actions.no_requisites_to_update')
              : t('actions.batch_unblock_requisites')
            : t('actions.trader_filter_required')
        }
        Icon={CreditCardIcon}
        color="success"
        onClick={() =>
          setBatchUnblockDialogProps({
            open: true,
            count: queryResult.data?.count,
          })
        }
        disabled={!filters?.traderId || noRequisitesToUpdate}
      />
    );
  }, [t, filters?.traderId, queryResult.data]);

  const renderRequisitesAutomationAction = useCallback(
    (requisites: Requisites) =>
      isTrader && (
        <IconButton
          color="primary"
          onClick={() => {
            handleOrderAutomation(requisites);
          }}
        >
          <AutoModeIcon />
        </IconButton>
      ),
    [isTrader, handleOrderAutomation],
  );

  const renderRequisitesCustomerLimitsConfigAction = useCallback(
    (requisites: Requisites) =>
      (isManager || isTrader) && (
        <Tooltip title={t('actions.customer_limits')}>
          <IconButton
            color="primary"
            onClick={() => handleCustomerLimits(requisites)}
          >
            <PeopleIcon />
          </IconButton>
        </Tooltip>
      ),
    [handleCustomerLimits, isManager, isTrader, t],
  );

  const renderRequisitesStatisticsAction = useCallback(
    (requisites: Requisites) =>
      (isManager || isTrader) && (
        <Tooltip title={t('actions.statistics')}>
          <IconButton
            color="primary"
            onClick={() => handleStatistics(requisites)}
          >
            <QueryStatsIcon />
          </IconButton>
        </Tooltip>
      ),
    [handleStatistics, isManager, isTrader, t],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<Requisites>[] => [
      {
        header: t('table.columns.status'),
        valueGetter: (item: Requisites) => (
          <div className="tw-flex tw-items-center tw-flex-nowrap">
            <RequisitesStatusInfo
              requisites={item}
              canChangeStatus={(isAdminOrTechOperator || isTrader) && !archived}
              updateStatus={handleStatusChange}
            />
            <RequisitesVerificationInfo requisites={item} />
            {item.inactiveStatusReason && (
              <Tooltip
                title={`${tCommon(
                  'features.requisites.error.inactive_status_reason.prefix',
                )}: ${tCommon(
                  `features.requisites.error.inactive_status_reason.${item.inactiveStatusReason}`,
                )}`}
              >
                <InfoOutlinedIcon color="warning" />
              </Tooltip>
            )}
            {!isTradeMethodSupported(item) && (
              <Tooltip
                title={tCommon(
                  'features.requisites.error.trade_method_unavailable',
                )}
              >
                <WarningAmberIcon color="warning" />
              </Tooltip>
            )}
          </div>
        ),
      },
      dataGridColumns.getIdColumn(),
      {
        header: t('table.columns.trader_name'),
        valueGetter: (item) => item.user?.name,
        hidden: isTrader,
      },
      {
        header: t('table.columns.requisites'),
        valueGetter: (item) => (
          <RequisitesInfo
            requisite={item}
            showLimitsInfoButton={
              !archived &&
              (isAdminOrTechOperator || isTrader) &&
              includes(
                BANKS_WITH_PAYMENT_LIMITS,
                find(banks, (bank) => bank.id === item.bankId)?.code,
              )
            }
            onLimitsInfoButtonClick={() =>
              setLimitsInfoDialogOpen({
                open: true,
                requisites: item,
              })
            }
          />
        ),
        valueClassName: 'tw-min-w-[200px]',
      },
      ...requisitesUtils.getLimitsColumns(),
      {
        header: t('table.columns.group'),
        valueKey: 'groupId',
        valueFormatter: (value) => getGroup(value)?.name,
        valueClassName: 'tw-w-[100px]',
      },
      {
        header: t('table.columns.order_automation_type'),
        valueGetter: (item) => {
          const group = getGroup(item.groupId);
          return (
            <Fragment>
              {!group && (
                <Fragment>
                  {requisitesUtils.getOrderAutomationTypeLabel(
                    item.orderAutomationType,
                  )}
                </Fragment>
              )}
              {group && (
                <Fragment>
                  {requisitesUtils.getOrderAutomationTypeLabel(
                    group.orderAutomationType,
                  )}
                  <div>{`(${t('table.columns.group')})`}</div>
                </Fragment>
              )}
            </Fragment>
          );
        },
      },
      {
        header: t('table.columns.order_amount'),
        valueGetter: (item) => (
          <Fragment>
            {!!item.minOrderAmount && (
              <span>{`${tCommon('common.from')} ${formatUtils.formatMoney(
                item.minOrderAmount,
              )} `}</span>
            )}
            {!!item.maxOrderAmount && (
              <span>{`${tCommon('common.to')} ${formatUtils.formatMoney(
                item.maxOrderAmount,
              )}`}</span>
            )}
          </Fragment>
        ),
      },
      {
        header: t('table.columns.completed_orders_in_minutes'),
        valueGetter: (item) =>
          item.completedOrdersInMinutes ? (
            <div>
              <div>{`${item.completedOrdersInMinutes} ${tCommon(
                'common.minutes',
              )}`}</div>
              <DateLabel>
                {formatUtils.formatDate(item.lastCompletedOrderAt)}
              </DateLabel>
            </div>
          ) : (
            tCommon('common.disabled')
          ),
        valueClassName: 'tw-w-[150px]',
      },
      {
        header: t('table.columns.any_bank'),
        valueGetter: (item) => formatUtils.formatBoolean(!item.sameBank),
      },
      {
        header: t('table.columns.counterparties_only'),
        valueGetter: (item) =>
          formatUtils.formatBoolean(item.counterpartiesOnly),
      },
      dataGridColumns.getActionsColumn({
        hidden: archived,
        children: (item) => (
          <Fragment>
            {renderRequisitesStatisticsAction(item)}
            {renderRequisitesCustomerLimitsConfigAction(item)}
            {renderVerifyRequisitesAction(item)}
            {renderBlockRequisitesAction(item)}
            {renderUnblockRequisitesAction(item)}
            {renderRequisitesAutomationAction(item)}
          </Fragment>
        ),
        handleEdit: isAdminOrTechOperator || isTrader ? handleEdit : undefined,
        editButtonProps: (item) => ({
          disabled: item.status === RequisitesStatus.Blocked,
        }),
        handleRemove:
          isAdminOrTechOperator || isTrader ? handleRemove : undefined,
      }),
    ],
    [
      t,
      isTrader,
      archived,
      isAdminOrTechOperator,
      handleEdit,
      handleRemove,
      handleStatusChange,
      tCommon,
      isTradeMethodSupported,
      banks,
      getGroup,
      renderRequisitesCustomerLimitsConfigAction,
      renderVerifyRequisitesAction,
      renderBlockRequisitesAction,
      renderUnblockRequisitesAction,
      renderRequisitesAutomationAction,
      renderRequisitesStatisticsAction,
    ],
  );

  const filtersDefinitions: FilterDefinition<RequisitesFilters>[] = useMemo(
    () => [
      ...filtersUtils.getDataRangeFilters(
        filters,
        FilterDefinitionType.DateTimeRange,
        t('filters.blocked_date'),
        'blockedFrom',
        'blockedTo',
      ),
      {
        label: t('filters.id'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: t('filters.trader'),
        name: 'traderId',
        type: FilterDefinitionType.Trader,
        traders: queryResultTraders.data,
        hidden: isTrader,
      },
      {
        label: t('filters.verification_status'),
        name: 'verificationStatus',
        type: FilterDefinitionType.Enum,
        enum: RequisitesVerificationStatus,
        getDisplayName: requisitesUtils.getVerificationStatusLabel,
        hidden: archived,
      },
      {
        label: t('filters.status'),
        name: 'status',
        type: FilterDefinitionType.Select,
        options: map(
          requisitesUtils.getStatusOptions(archived),
          (value: RequisitesStatus) => ({
            value,
            label: requisitesUtils.getStatusLabel(value),
          }),
        ),
        getDisplayName: requisitesUtils.getStatusLabel,
      },
      filtersUtils.getBooleanStatusFilter<RequisitesFilters>(
        t('filters.counterparties_only'),
        'counterpartiesOnly',
      ),
      {
        label: t('filters.bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        getDisplayName: (bankId: string) => find(banks, { id: bankId })?.name,
      },
      {
        label: t('filters.payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        getDisplayName: (paymentType: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: paymentType })!,
          ),
      },
      {
        label: t('filters.group'),
        name: 'groupId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(groups),
        getDisplayName: (groupId: string) =>
          find(groups, { id: groupId })?.name,
      },
      {
        label: t('filters.fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
      },
      ...requisitesUtils.getRequisitesFieldsFilters<RequisitesFilters>(),
    ],
    [
      t,
      queryResultTraders.data,
      isTrader,
      banks,
      archived,
      filters,
      groups,
      paymentTypes,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
    ],
  );

  const { mutate: updateOrderAutomation } = useMutation<
    Requisites,
    AxiosError,
    { id: string; requisites: Values }
  >(requisitesApi.updateOrderAutomation);

  const handleCloseOrderAutomationDialog = useCallback(
    (result: CloseDialogResult<Requisites>) => {
      const options = {
        onSuccess: () => {
          setOrderAutomationDialogProps({ open: false });
          queryClient.invalidateQueries(QueryKey.Requisites);
        },
        onError: (error: AxiosError) => {}, // handle save error
      };

      if (result.ok && result.data) {
        updateOrderAutomation(
          {
            id: result.data.id,
            requisites: { orderAutomationType: selectedOrderAutomationType },
          },
          options,
        );
      } else {
        setOrderAutomationDialogProps({ open: false });
      }
    },
    [selectedOrderAutomationType, updateOrderAutomation, queryClient],
  );

  const handleVerificationDialogClose = useCallback(
    (result: CloseDialogResult) => {
      if (result.ok) {
        queryClient.invalidateQueries(QueryKey.Requisites);
      }
      setRequisitesVerificationDialogProps({ open: false });
    },
    [queryClient],
  );

  const handleBatchVerificationDialogClose = useCallback(
    (result: CloseDialogResult) => {
      if (result.ok) {
        queryClient.invalidateQueries(QueryKey.Requisites);
      }
      setBatchRequisitesVerificationDialogProps({ open: false });
    },
    [queryClient],
  );

  const handleCloseRemoveDialog = useCallback(
    (result: CloseDialogResult<Requisites>) => {
      if (result.ok && result.data) {
        archive(result.data.id);
      } else {
        setConfirmRemoveDialogProps({ open: false });
      }
    },
    [archive],
  );

  return (
    <Fragment>
      <CrudPage
        filters={{ filtersDefinitions }}
        table={{
          queryResult,
          paginated: true,
          columns,
          ignoreState: { refetchLoading: true },
        }}
        afterFiltersContent={
          isManager &&
          !archived && (
            <Fragment>
              {renderBatchVerifyAction()}
              {renderBatchBlockAction()}
              {renderBatchUnblockAction()}
            </Fragment>
          )
        }
      />

      {isTrader && (
        <TraderRequisitesVerificationDialog
          {...requisitesVerificationDialogProps}
          onClose={handleVerificationDialogClose}
        />
      )}

      {!archived && (isAdminOrTechOperator || isTrader) && (
        <PayinLimitsInfoDialog
          {...limitsInfoDialogProps}
          onClose={() => setLimitsInfoDialogOpen({ open: false })}
        />
      )}

      <Dialog
        title={t('order_automation_dialog.title')}
        disabled={isLoading}
        onClose={handleCloseOrderAutomationDialog}
        okLabel={tCommon('buttons.save')}
        {...orderAutomationDialogProps}
      >
        {orderAutomationDialogProps.data && (
          <Stack direction="column" spacing={6}>
            <QrCode data={orderAutomationDialogProps.qrCodeData} />
            <DescriptionRow
              title={t('order_automation_dialog.api_token')}
              value={orderAutomationDialogProps.data.automationApiKey}
              canCopy
            />
            <Stack direction="column" spacing={0.5}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('order_automation_dialog.automation_type')}
              </Typography>
              {!selectedRequisiteGroup && (
                <ToggleButtonGroup
                  color="primary"
                  value={selectedOrderAutomationType}
                  exclusive
                  size="small"
                  onChange={handleAutomationTypeChange}
                >
                  <ToggleButton value={OrderAutomationType.Inactive}>
                    {t('order_automation_types.inactive')}
                  </ToggleButton>
                  <ToggleButton value={OrderAutomationType.Manual}>
                    {t('order_automation_types.manual')}
                  </ToggleButton>
                  <ToggleButton value={OrderAutomationType.Active}>
                    {t('order_automation_types.active')}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              {selectedRequisiteGroup && (
                <div>
                  {requisitesUtils.getOrderAutomationTypeLabel(
                    selectedRequisiteGroup.orderAutomationType,
                  )}
                  <span>{` (${t('table.columns.group')})`}</span>
                </div>
              )}
            </Stack>
          </Stack>
        )}
      </Dialog>

      <RequisitesStatisticsDialog
        {...statisticsDialogProps}
        onClose={() => setStatisticsDialogProps({ open: false })}
      />

      {isManager && (
        <Fragment>
          <ManagerRequisitesVerificationDialog
            {...requisitesVerificationDialogProps}
            onClose={handleVerificationDialogClose}
          />
          <BatchManagerRequisitesVerificationDialog
            {...batchRequisitesVerificationDialogProps}
            onClose={handleBatchVerificationDialogClose}
          />
          <BlockRequisitesDialog
            {...blockDialogProps}
            onClose={handleCloseBlockDialog}
          />
          <BatchBlockRequisitesDialog
            {...batchBlockDialogProps}
            onClose={handleCloseBatchBlockDialog}
          />
          <UnblockRequisitesDialog
            {...unblockDialogProps}
            onClose={handleCloseUnblockDialog}
          />
          <BatchUnblockRequisitesDialog
            {...batchUnblockDialogProps}
            onClose={handleCloseBatchUnblockDialog}
          />
        </Fragment>
      )}

      <Dialog
        title={t('remove_dialog.title')}
        disabled={isLoading}
        onClose={handleCloseRemoveDialog}
        {...confirmRemoveDialogProps}
      >
        <Fragment>
          {confirmRemoveDialogProps.data && (
            <RequisitesInfo
              bankId={confirmRemoveDialogProps.data.bankId}
              cardInfo={confirmRemoveDialogProps.data.cardInfo}
              phone={confirmRemoveDialogProps.data.phone}
              paymentTypeId={confirmRemoveDialogProps.data.paymentTypeId}
              cardholder={confirmRemoveDialogProps.data.cardholder}
              swiftBic={confirmRemoveDialogProps.data.swiftBic}
              bic={confirmRemoveDialogProps.data.bic}
              email={confirmRemoveDialogProps.data.email}
              idCard={confirmRemoveDialogProps.data.idCard}
              beneficiaryName={confirmRemoveDialogProps.data.beneficiaryName}
              accountNumber={confirmRemoveDialogProps.data.accountNumber}
              name={confirmRemoveDialogProps.data.name}
              expirationDate={confirmRemoveDialogProps.data.expirationDate}
              taxId={confirmRemoveDialogProps.data.taxId}
              paymentLink={confirmRemoveDialogProps.data.paymentLink}
              accountNumberQrCodeGenerated={
                confirmRemoveDialogProps.data.accountNumberQrCodeGenerated
              }
            />
          )}
        </Fragment>
      </Dialog>
    </Fragment>
  );
};
